// profileStore.ts — two-tier profile system used by both SceneBuilder and
// IsometricBuilder.
//
// SOURCES
//   "server"  → public/profiles/<slug>.json. Edits via Vite dev endpoint
//               (localhost only). Read-only for visitors.
//   "local"   → localStorage["oneqode-local-profiles-v1"]. Always writable.
//               Visitor-only scratch space; never leaves the browser.
//
// IDENTITY
//   ProfileRef = { source, slug, name, viewKind }
//   viewKind ∈ {"scene","iso"}
//   slug for server profiles must start with "scene-" or "iso-".
//   "Main (Reiss)" maps to slugs scene-main and iso-main and is canonical.

const SAVE_DEBOUNCE_MS = 600;
const LOCAL_KEY = "oneqode-local-profiles-v1";

const BASE_URL = (import.meta as any).env?.BASE_URL ?? "/";
const profilePublicPath = (slug: string) =>
  `${BASE_URL.replace(/\/$/, "")}/profiles/${slug}.json`;

export type ViewKind = "scene" | "iso";
export type ProfileSource = "server" | "local";

export type ProfileRef = {
  source: ProfileSource;
  slug: string;        // unique within (source, viewKind)
  name: string;        // display name
  viewKind: ViewKind;
};

// ============================================================
// Editor mode
// ============================================================

export function isLocalEditor(): boolean {
  if (typeof window === "undefined") return false;
  const isDev = !!(import.meta as any).env?.DEV;
  const host = window.location.hostname;
  const isLocal = host === "localhost" || host === "127.0.0.1" || host === "::1";
  return isDev && isLocal;
}

// ============================================================
// Server profiles
// ============================================================

/**
 * List server-side profile JSONs. In editor mode this hits the dev /__profiles
 * endpoint; in production the canonical Main slugs are returned plus any other
 * profiles known to ship with the build via a static `profiles/index.json`
 * (which we don't currently emit — production visitors only see Main).
 */
export async function listServerProfiles(): Promise<Array<{
  slug: string;
  kind: "scene" | "iso";
}>> {
  if (isLocalEditor()) {
    try {
      const r = await fetch("/__profiles", { cache: "no-cache" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const arr = (await r.json()) as Array<{ slug: string; kind: string }>;
      return arr
        .filter((e) => e.kind === "scene" || e.kind === "iso")
        .map((e) => ({ slug: e.slug, kind: e.kind as "scene" | "iso" }));
    } catch (e) {
      console.warn("[profileStore] /__profiles failed, falling back", e);
    }
  }
  // Production fallback: hardcoded list of canonical profiles shipped in
  // public/profiles/. Add new server profiles here when they're committed
  // to the repo so visitors (off-localhost) discover them.
  return [
    { slug: "scene-main", kind: "scene" },
    { slug: "iso-main",   kind: "iso" },
    { slug: "iso-home",   kind: "iso" },
  ];
}

export async function fetchServerProfile<T = unknown>(slug: string): Promise<T | null> {
  try {
    const r = await fetch(profilePublicPath(slug), { cache: "no-cache" });
    if (!r.ok) return null;
    return (await r.json()) as T;
  } catch (e) {
    console.warn(`[profileStore] fetch ${slug} failed`, e);
    return null;
  }
}

export async function saveServerProfile(
  slug: string,
  payload: unknown,
): Promise<{ ok: boolean; error?: string }> {
  if (!isLocalEditor()) return { ok: false, error: "read-only" };
  try {
    const r = await fetch(`/__save-profile/${slug}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!r.ok) return { ok: false, error: `HTTP ${r.status}: ${await r.text()}` };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

export async function deleteServerProfile(
  slug: string,
): Promise<{ ok: boolean; error?: string }> {
  if (!isLocalEditor()) return { ok: false, error: "read-only" };
  if (slug === "scene-main" || slug === "iso-main") {
    return { ok: false, error: "Cannot delete canonical Main profile" };
  }
  try {
    const r = await fetch(`/__delete-profile/${slug}`, { method: "DELETE" });
    if (!r.ok) return { ok: false, error: `HTTP ${r.status}` };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

// ============================================================
// Local profiles (localStorage)
// ============================================================

type LocalEntry = {
  slug: string;       // unique within viewKind
  viewKind: ViewKind;
  name: string;
  payload: unknown;   // the same shape as the corresponding server JSON
  updatedAt: number;
};

type LocalStore = { profiles: LocalEntry[] };

function readLocal(): LocalStore {
  if (typeof window === "undefined") return { profiles: [] };
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return { profiles: [] };
    const parsed = JSON.parse(raw) as LocalStore;
    if (!parsed || !Array.isArray(parsed.profiles)) return { profiles: [] };
    return parsed;
  } catch {
    return { profiles: [] };
  }
}

function writeLocal(store: LocalStore): void {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(store));
  } catch (e) {
    console.error("[profileStore] failed to save local store", e);
  }
}

export function listLocalProfiles(viewKind: ViewKind): LocalEntry[] {
  return readLocal().profiles.filter((p) => p.viewKind === viewKind);
}

export function fetchLocalProfile<T = unknown>(
  viewKind: ViewKind,
  slug: string,
): T | null {
  const e = readLocal().profiles.find(
    (p) => p.viewKind === viewKind && p.slug === slug,
  );
  return (e?.payload as T) ?? null;
}

export function saveLocalProfile(
  viewKind: ViewKind,
  slug: string,
  name: string,
  payload: unknown,
): void {
  const store = readLocal();
  const i = store.profiles.findIndex(
    (p) => p.viewKind === viewKind && p.slug === slug,
  );
  const entry: LocalEntry = { viewKind, slug, name, payload, updatedAt: Date.now() };
  if (i >= 0) store.profiles[i] = entry;
  else store.profiles.push(entry);
  writeLocal(store);
}

export function deleteLocalProfile(viewKind: ViewKind, slug: string): void {
  const store = readLocal();
  store.profiles = store.profiles.filter(
    (p) => !(p.viewKind === viewKind && p.slug === slug),
  );
  writeLocal(store);
}

export function renameLocalProfile(
  viewKind: ViewKind,
  slug: string,
  name: string,
): void {
  const store = readLocal();
  const e = store.profiles.find((p) => p.viewKind === viewKind && p.slug === slug);
  if (!e) return;
  e.name = name;
  e.updatedAt = Date.now();
  writeLocal(store);
}

// ============================================================
// Unified ref helpers
// ============================================================

export function makeServerRef(viewKind: ViewKind, slug: string, name: string): ProfileRef {
  return { source: "server", slug, name, viewKind };
}
export function makeLocalRef(viewKind: ViewKind, slug: string, name: string): ProfileRef {
  return { source: "local", slug, name, viewKind };
}

/**
 * Generate a fresh local slug for a given view, e.g. "scene-local-h7pe2t".
 */
export function newLocalSlug(viewKind: ViewKind): string {
  return `${viewKind}-local-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Generate a slug from a user-supplied display name. Intended for new server
 * profiles created on localhost (e.g. "Storage zoom" → "scene-storage-zoom").
 */
export function slugFromName(viewKind: ViewKind, name: string): string {
  const base = name.toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "untitled";
  return `${viewKind}-${base}`;
}

// ============================================================
// Autosaver — debounced write to whichever source the ref points at
// ============================================================

export function makeAutoSaver() {
  let pendingRef: ProfileRef | null = null;
  let pendingPayload: unknown = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastStatus: "idle" | "saving" | "saved" | "error" = "idle";
  const subs = new Set<(s: typeof lastStatus, msg?: string) => void>();

  function emit(s: typeof lastStatus, msg?: string) {
    lastStatus = s;
    subs.forEach((cb) => cb(s, msg));
  }

  async function flush(): Promise<void> {
    const ref = pendingRef;
    const payload = pendingPayload;
    if (!ref || payload == null) return;
    pendingRef = null;
    pendingPayload = null;
    emit("saving");
    if (ref.source === "server") {
      const r = await saveServerProfile(ref.slug, payload);
      if (r.ok) emit("saved"); else emit("error", r.error);
    } else {
      saveLocalProfile(ref.viewKind, ref.slug, ref.name, payload);
      emit("saved");
    }
  }

  function trigger(ref: ProfileRef, payload: unknown): void {
    pendingRef = ref;
    pendingPayload = payload;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => { void flush(); }, SAVE_DEBOUNCE_MS);
  }

  function subscribe(cb: (s: typeof lastStatus, msg?: string) => void): () => void {
    subs.add(cb);
    cb(lastStatus);
    return () => { subs.delete(cb); };
  }

  return { trigger, flush, subscribe };
}

// ============================================================
// Default ref helpers — used by both builders to pick the active profile
// on first load.
// ============================================================

const ACTIVE_KEY = (viewKind: ViewKind) => `oneqode-active-profile-${viewKind}`;

export function readActiveRef(viewKind: ViewKind): ProfileRef | null {
  try {
    const raw = localStorage.getItem(ACTIVE_KEY(viewKind));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ProfileRef;
    if (parsed.viewKind !== viewKind) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeActiveRef(ref: ProfileRef): void {
  try {
    localStorage.setItem(ACTIVE_KEY(ref.viewKind), JSON.stringify(ref));
  } catch {}
  // Notify App so it can mirror the change into the URL hash for
  // shareable deep-links. Custom event because there's no React store.
  try {
    window.dispatchEvent(new CustomEvent("oneqode:active-ref", { detail: ref }));
  } catch {}
}

/**
 * Default for first load: Main (Reiss) on the server.
 */
export function defaultMainRef(viewKind: ViewKind): ProfileRef {
  return makeServerRef(viewKind, `${viewKind}-main`, "Main (Reiss)");
}
